/**
 * The set of options we can use when editing an image
 */
export interface ImageEditorOptions {
  /**
   * The maximum number of pixels we allow for an image.
   * @remarks An images that exceed this number will be scaled down in either axis to fit.
   */
  maxSizeInPixels?: number;
  /**
   * The minimum width (in pixels) we allow for an image.
   * @remarks Any images smaller than this will be rejected.
   */
  minWidthInPixels?: number;
  /**
   * The minimum height (in pixels) we allow for an image.
   * @remarks Any images smaller than this will be rejected.
   */
  minHeightInPixels?: number;
  /**
   * The maximum value we allow for zooming in.
   */
  maxZoom?: number;
  /**
   * The minimum value we allow for zooming out.
   */
  minZoom?: number;
  /**
   * How high the cropped image will be, relative to the width.
   */
  aspectRatioHeight?: number;
  /**
   * How wide the cropped image will be, relative to the height.
   */
  aspectRatioWidth?: number;
  /**
   * The largest file size in bytes we accept.
   */
  maxFileSizeInBytes?: number;
}

/**
 * Get the default set of values to use in image editor options
 */
const defaultImageEditorOptions = {
  maxSizeInPixels: 4000,
  minWidthInPixels: 480,
  minHeightInPixels: 270,
  maxZoom: 4,
  minZoom: 1,
  aspectRatioHeight: 9,
  aspectRatioWidth: 16,
  maxFileSizeInBytes: 5242880
};

/**
 * Get a set of image editor options, with default values for any missing options.
 * @param options The set of options to use.
 */
export function getImageEditorOptions(options?: ImageEditorOptions) {
  return {...defaultImageEditorOptions, ...options};
}
